import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add,
  CloudUpload,
  Delete,
  Edit,
  Download,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { supabase } from '../lib/supabase';

interface Document {
  id: string;
  title: string;
  description: string;
  category: string;
  file_name: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  is_active: boolean;
  published_date: string;
  download_count: number;
  tags: string[];
  created_at: string;
  updated_at: string;
}

const FilesPage: React.FC = () => {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<Document | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('other');
  const [isActive, setIsActive] = useState(true);
  const [tags, setTags] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const categories = [
    { value: 'forms', label: t('documents.categories.forms') },
    { value: 'regulations', label: t('documents.categories.regulations') },
    { value: 'guidelines', label: t('documents.categories.guidelines') },
    { value: 'reports', label: t('documents.categories.reports') },
    { value: 'announcements', label: t('documents.categories.announcements') },
    { value: 'certificates', label: t('documents.categories.certificates') },
    { value: 'manuals', label: t('documents.categories.manuals') },
    { value: 'other', label: t('documents.categories.other') },
  ];

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .order('published_date', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (document?: Document) => {
    if (document) {
      setEditingDocument(document);
      setTitle(document.title);
      setDescription(document.description || '');
      setCategory(document.category);
      setIsActive(document.is_active);
      setTags(document.tags?.join(', ') || '');
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setEditingDocument(null);
    setTitle('');
    setDescription('');
    setCategory('other');
    setIsActive(true);
    setTags('');
    setSelectedFile(null);
    setError(null);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const uploadFile = async (file: File): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('documents')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) throw uploadError;
    return filePath;
  };

  const handleSubmit = async () => {
    if (!title) {
      setError(t('documents.titleRequired'));
      return;
    }

    if (!editingDocument && !selectedFile) {
      setError(t('documents.fileRequired'));
      return;
    }

    setUploading(true);
    setError(null);

    try {
      let filePath = editingDocument?.file_path;
      let fileName = editingDocument?.file_name;
      let fileSize = editingDocument?.file_size;
      let mimeType = editingDocument?.mime_type;

      // Upload new file if selected
      if (selectedFile) {
        filePath = await uploadFile(selectedFile);
        fileName = selectedFile.name;
        fileSize = selectedFile.size;
        mimeType = selectedFile.type;
      }

      // Process tags
      const tagsArray = tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      const documentData = {
        title,
        description,
        category,
        file_name: fileName,
        file_path: filePath,
        file_size: fileSize,
        mime_type: mimeType,
        is_active: isActive,
        tags: tagsArray.length > 0 ? tagsArray : null,
      };

      if (editingDocument) {
        // Update existing document
        const { error } = await supabase
          .from('documents')
          .update(documentData)
          .eq('id', editingDocument.id);

        if (error) throw error;
        setSuccess(t('documents.updatedSuccess'));
      } else {
        // Create new document
        const { error } = await supabase
          .from('documents')
          .insert([documentData]);

        if (error) throw error;
        setSuccess(t('documents.uploadedSuccess'));
      }

      handleCloseDialog();
      fetchDocuments();
    } catch (err: any) {
      console.error('Error saving document:', err);
      setError(err.message || t('documents.errorSaving'));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string, filePath: string) => {
    if (!window.confirm(t('documents.deleteConfirm'))) {
      return;
    }

    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('documents')
        .remove([filePath]);

      if (storageError) console.error('Storage delete error:', storageError);

      // Delete from database
      const { error: dbError } = await supabase
        .from('documents')
        .delete()
        .eq('id', id);

      if (dbError) throw dbError;

      setSuccess(t('documents.deletedSuccess'));
      fetchDocuments();
    } catch (err: any) {
      console.error('Error deleting document:', err);
      setError(err.message || t('documents.errorDeleting'));
    }
  };

  const handleDownload = async (filePath: string, fileName: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('documents')
        .download(filePath);

      if (error) throw error;

      const url = window.URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      console.error('Error downloading file:', err);
      setError(t('documents.errorDownloading'));
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const columns: GridColDef[] = [
    { 
      field: 'title', 
      headerName: t('documents.documentTitle'), 
      flex: 1, 
      minWidth: 200 
    },
    {
      field: 'category',
      headerName: t('documents.category'),
      width: 150,
      renderCell: (params) => {
        const cat = categories.find(c => c.value === params.value);
        return <Chip label={cat?.label || params.value} size="small" />;
      },
    },
    {
      field: 'file_name',
      headerName: t('documents.fileName'),
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'file_size',
      headerName: t('documents.size'),
      width: 120,
      renderCell: (params) => formatFileSize(params.value || 0),
    },
    {
      field: 'download_count',
      headerName: t('documents.downloads'),
      width: 100,
      align: 'center',
    },
    {
      field: 'is_active',
      headerName: t('documents.active'),
      width: 100,
      renderCell: (params) => (
        <Chip 
          label={params.value ? t('documents.yes') : t('documents.no')} 
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: t('table.actions'),
      width: 150,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleDownload(params.row.file_path, params.row.file_name)}
            title={t('documents.download')}
          >
            <Download fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleOpenDialog(params.row)}
            title={t('documents.edit')}
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row.id, params.row.file_path)}
            title={t('documents.delete')}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">{t('documents.title')}</Typography>
        <Button
          variant="contained"
          onClick={() => handleOpenDialog()}
          sx={{ gap: 1 }}
        >
          <Add sx={{ fontSize: 18 }} />
          {t('documents.uploadDocument')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <DataGrid
            rows={documents}
            columns={columns}
            loading={loading}
            autoHeight
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
          />
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingDocument ? t('documents.editDocument') : t('documents.uploadNewDocument')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label={t('documents.documentTitle')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              required
            />

            <TextField
              label={t('documents.description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
            />

            <FormControl fullWidth>
              <InputLabel>{t('documents.category')}</InputLabel>
              <Select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                label={t('documents.category')}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={t('documents.tags')}
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              fullWidth
              placeholder={t('documents.tagsPlaceholder')}
              helperText={t('documents.tagsHelper')}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              }
              label={t('documents.active')}
            />

            <Box>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ gap: 1 }}
              >
                <CloudUpload sx={{ fontSize: 18 }} />
                {selectedFile 
                  ? selectedFile.name 
                  : editingDocument 
                    ? t('documents.changeFile') 
                    : t('documents.selectFile')
                }
                <input
                  type="file"
                  hidden
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.zip,.rar,.txt"
                />
              </Button>
              {selectedFile && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {t('documents.selected')}: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                </Typography>
              )}
              {editingDocument && !selectedFile && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {t('documents.currentFile')}: {editingDocument.file_name}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('documents.cancel')}</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={uploading}
            sx={{ gap: 1 }}
          >
            {uploading ? <CircularProgress size={20} color="inherit" /> : <CloudUpload sx={{ fontSize: 18 }} />}
            {uploading 
              ? t('documents.uploading') 
              : editingDocument 
                ? t('documents.update') 
                : t('documents.upload')
            }
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FilesPage;
